import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UploadField } from "./UploadField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REMIX_TYPES = ["Remix", "Bootleg", "Extended Bootleg", "Edit", "Mashup", "Rework"];
const inputCls = "bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-white";

const emptyForm = (count) => ({
  title: "", original_artist: "", remix_type: "Remix", artwork_path: "", artwork_url: "",
  audio_path: "", audio_url: "", audio_filename: "", audio_size: 0, preview_path: "", preview_url: "", sort_order: count,
});

export const RemixDialog = ({ open, onOpenChange, editing, token, count, onSaved }) => {
  const [form, setForm] = useState(emptyForm(count));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? {
      title: editing.title, original_artist: editing.original_artist || "", remix_type: editing.remix_type || "Remix",
      artwork_path: editing.artwork_path || "", artwork_url: editing.artwork_url || "",
      audio_path: editing.audio_path || "", audio_url: editing.audio_url || "",
      audio_filename: editing.audio_filename || "", audio_size: editing.audio_size || 0,
      preview_path: editing.preview_path || "", preview_url: editing.preview_url || "", sort_order: editing.sort_order ?? 0,
    } : emptyForm(count));
  }, [open, editing, count]);

  const hasArtwork = form.artwork_path || form.artwork_url;
  const hasAudio = form.audio_path || form.audio_url;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasAudio) { toast.error("Please upload the audio file"); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editing) await axios.put(`${API}/remixes/${editing.id}`, form, { headers });
      else await axios.post(`${API}/remixes`, form, { headers });
      toast.success(editing ? "Remix updated" : "Remix added");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(editing ? "Failed to update remix" : "Failed to add remix");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tighter">{editing ? "EDIT REMIX" : "NEW REMIX / BOOTLEG"}</DialogTitle>
          <DialogDescription className="font-sub text-[10px] text-white/40 tracking-widest">
            FREE DOWNLOAD SHOWN IN THE REMIXES SECTION
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          <div>
            <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">TRACK TITLE</Label>
            <Input data-testid="remix-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder="e.g. Good Things Come To An End" required />
          </div>
          <div>
            <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">ORIGINAL ARTIST</Label>
            <Input data-testid="remix-artist-input" value={form.original_artist} onChange={(e) => setForm({ ...form, original_artist: e.target.value })} className={inputCls} placeholder="e.g. Nelly Furtado" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">TYPE</Label>
              <select
                data-testid="remix-type-select"
                value={form.remix_type}
                onChange={(e) => setForm({ ...form, remix_type: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white text-sm focus:outline-none focus:border-white"
              >
                {REMIX_TYPES.map((t) => <option key={t} value={t} className="bg-[#0a0a0a]">{t}</option>)}
              </select>
            </div>
            <div>
              <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-2 block">SORT ORDER</Label>
              <Input data-testid="remix-order-input" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} className={inputCls} />
            </div>
          </div>
          <UploadField
            label="ARTWORK (JPG/PNG, SQUARE)"
            accept="image/jpeg,image/png,image/webp"
            token={token}
            testId="remix-artwork-upload"
            value={hasArtwork ? (form.artwork_url || "Artwork uploaded") : ""}
            onUploaded={(r) => setForm({ ...form, artwork_path: r.path, artwork_url: "" })}
            hint="Leave empty to use the default Alchemist artwork."
          />
          <UploadField
            label="AUDIO FILE (WAV/MP3/AIFF/FLAC)"
            accept=".wav,.mp3,.aif,.aiff,.flac,audio/*"
            token={token}
            testId="remix-audio-upload"
            value={hasAudio ? (form.audio_filename || "Audio uploaded") : ""}
            onUploaded={(r) => setForm({ ...form, audio_path: r.path, audio_url: "", audio_filename: r.filename, audio_size: r.size, preview_path: r.preview_path || "", preview_url: "" })}
            hint={
              hasAudio && (form.preview_path || form.preview_url)
                ? "30-second preview ready — visitors can play it on the tile."
                : "Up to 300 MB. A 30-second preview is generated automatically after upload."
            }
          />
          <button
            data-testid="remix-submit-btn"
            type="submit"
            disabled={saving}
            className="btn-alchemist w-full border border-white/20 bg-white/5 hover:bg-white hover:text-black py-4 px-8 uppercase tracking-widest text-xs font-sub transition-colors duration-300 mt-2 disabled:opacity-40"
          >
            {saving ? "Saving..." : editing ? "Update Remix" : "Add Remix"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
